// @flow
/*global Response*/

import { location, sessionStorage } from 'window';
import console from 'console';

import url from 'url';

import invariant from 'invariant';

import {
	Error,
	enrichNative as enrichNativeError,
} from 'error';

import _ from 'lodash';

import Q from 'q';
import type {
	QPromise,
} from 'q';

import fetch from 'fetch';
import { createClient } from '@rgssup/fetch-plus';

import type { LogContextInterface } from 'log-context';
import type { AuthContextInterface } from 'auth-context';

import serverApiVersioning from 'server-api-versioning';
import clientSource from 'client-source';

import { root as rootLogContext } from 'log-context';

import config from 'config';

import util from 'server/js/util';



import { restRoute } from './known-rest-routes';

export { HttpErrorsHelper } from './http-errors-helper';

export { KnownErrorsHelper } from './known-errors-helper';

export * from './known-rest-routes';

import type {
	FetchPlusRequestOptions,
	PublicOptions,
	FetchPlusRequest,
	Middleware,
	Method,
} from './types.flow.js';

import type {
	ServerResponse,
	ServerDataResponse,
} from './contracts.flow.js';

export type {
	PublicOptions as Options,
	ServerResponse as Response,
	ServerDataResponse as DataResponse,
};

export interface FetchPlusClientInterface {
	+get: Method;
	+patch: Method;
	+post: Method;
	+put: Method;
	+del: Method; // createClient возврещает del а не delete
}

export type Captcha = {
	key: String;
	value: String;
}

export type YaCaptcha = {
	token: string;
}

export type StatisticsHeadersInterface = {
	'source-app': String;
	'source-platform': String;
}

export type StatisticsQueriesInterface = {
	'rgstm_source'?: String;
}

export interface ClientInterface extends FetchPlusClientInterface {
	withFetch (fetch: typeof fetch): ClientInterface;
	withBaseUrl (string): ClientInterface;
	withAddedMiddleware (Middleware): ClientInterface;
	withAddedOptions (PublicOptions): ClientInterface;
	withAddedPath (Array<string>): ClientInterface;
	withLogContext (logContext: LogContextInterface): ClientInterface;
	withAuthContext (authContext: AuthContextInterface): ClientInterface;
	withCaptcha (captcha: Captcha): ClientInterface;
	withYaCaptcha (captcha: YaCaptcha): ClientInterface;
	withStatisticsHeaders (headers: StatisticsHeadersInterface): ClientInterface;
	withStatisticsQueries (queries: StatisticsQueriesInterface): ClientInterface;
}

class Client {
	_baseUrl: string = '';
	_fetch: * = fetch;
	_options: FetchPlusRequestOptions = {};
	_middlewares: Array<Middleware> = [];

	get _client (): FetchPlusClientInterface {
		(this._options: any).fetch = (...args) => {
			// WORKAROUND: https://github.com/RickWong/fetch-plus/issues/34
			if (args.length === 0) {
				return;
			}
			return this._fetch(...args);
		};

		return createClient(this._baseUrl, this._options, this._middlewares);
	}

	_clone (modify?: (Client) => void): Client {
		const client = new Client();
		client._baseUrl = this._baseUrl;
		client._options = this._options;
		client._fetch = this._fetch;
		client._middlewares = this._middlewares;
		if (modify) {
			modify(client);
		}
		return client;
	}

	withFetch (fetch: typeof fetch): Client {
		return this._clone(client => {
			client._fetch = fetch;
		});
	}

	withBaseUrl (baseUrl: string): Client {
		return this._clone(client => {
			client._baseUrl = baseUrl;
		});
	}

	withAddedPath (path: Array<string>): Client {
		return this._clone(client => {
			client._baseUrl += '/' + path.join('/');
		});
	}

	withAddedMiddleware (middleware: Middleware): Client {
		return this._clone(client => {
			client._middlewares = client._middlewares.concat(middleware);
		});
	}

	withAddedOptions (options: PublicOptions): Client {
		return this._clone(client => {
			client._options = _.defaultsDeep({}, options, client._options);
		});
	}

	withLogContext (logContext: LogContextInterface): Client {
		return this.withAddedMiddleware(logContextMiddleware(logContext));
	}

	withAuthContext (authContext: AuthContextInterface): Client {
		return this.withAddedMiddleware(authContextMiddleware(authContext));
	}

	withCaptcha (captcha: Captcha): Client {
		return this.withAddedMiddleware(captchaMiddleware(captcha));
	}

	withYaCaptcha (captcha: YaCaptcha): Client {
		return this.withAddedMiddleware(yaCaptchaMiddleware(captcha));
	}

	withStatisticsHeaders (headers: StatisticsHeadersInterface): Client {
		return this.withAddedMiddleware(statisticsHeadersMiddleware(headers));
	}

	withStatisticsQueries (queries: StatisticsQueriesInterface): Client {
		return this.withAddedMiddleware(statisticsQueriesMiddleware(queries));
	}

	get (...args: *): * {
		return Q(this._client.get(...args));
	}

	put (...args: *): * {
		return Q(this._client.put(...args));
	}

	post (...args: *): * {
		return Q(this._client.post(...args));
	}

	patch (...args: *): * {
		return Q(this._client.patch(...args));
	}

	del (...args: *): * {
		return Q(this._client.del(...args));
	}
}

const bodyTypeJSONMiddleware = request => {
	if (!request.options.bodyType) {
		request.options.bodyType = 'json';
	}

	if (request.options.bodyType !== 'json') {
		return _.identity;
	}

	request.options.headers['content-type'] = 'application/json; charset=utf-8';
	request.options.body = JSON.stringify(request.options.body);

	return _.identity;
};

const apiTuningMiddleware = request => {
	// RGSSUP-27409, API 1.4. Suppress SKK codes as identity document types
	request.options.headers['x-pretty-identity-document-mode'] = true;

	return _.identity;
};

const markOfrFrameModeMiddleware = request => {
	const isOfrFrameMode = Boolean(sessionStorage.getItem('ofrLkdmsIframe'));
	if (isOfrFrameMode) {
		request.options.headers['X-Rgs-Ofr-Frame-Mode'] = true;
	}

	return _.identity;
};

const addTrailingSlashMiddleware = request => {
	request.path = request.path.replace(/\/?$/, '/');
	return _.identity;
};

const alwaysParseJSONMiddleware = () => async response => {
	try {
		response.responseJSON = await response.json();
	} catch (e) {
		console.warn('Could not parse response body', response, e);
	}
	return response;
};

const enrichNativeErrorMiddleware = () => ({
	after (response) {
		return response;
	},
	error (err) {
		throw enrichNativeError(err);
	},
});

const jsonEncodedErrorMiddleware = () => async response => {
	if (response.responseJSON) {
		const {
			Error: message,
			ErrorType: name,
			ErrorCode: code,
			Status: alternateName,
			ErrorMessagesForClient: userFriendlyMessages,
			ErrorCorrelationIds: correlationIds,
		} = response.responseJSON;
		if (message) {
			const e = new Error(message, {
				name: name || alternateName || 'AnonymousException',
				userFriendlyMessages,
				correlationIds: _.isEmpty(correlationIds) ? null : correlationIds,
				code,
				response,
			});
			throw e;
		}
	}
	return response;
};

const httpErrorMiddleware = () => async response => {
	if (!response.ok) {
		const e = new Error(response.statusText);
		e.name = 'HttpError';
		e.response = response;
		throw e;
	}
	return response;
};

const serverApiVersioningMiddleware = request => {
	_.extend(request.options.headers, serverApiVersioning.getAdditionalRequestHeaders());
	return _.identity;
};

const clientSourceMiddleware = request => {
	_.extend(request.options.headers, clientSource.getAdditionalRequestHeaders());
	return _.identity;
};

const authContextMiddleware = authContext => request => {
	_.extend(request.options.headers, authContext.getAdditionalRequestHeaders());
	return _.identity;
};

const logContextMiddleware = logContext => request => {
	_.extend(request.options.headers, logContext.getAdditionalRequestHeaders());
	return _.identity;
};

const captchaMiddleware = (captcha: Captcha) => (request: FetchPlusRequest) => {
	_.extend(request.options.headers, {
		'Captcha': `${captcha.key} ${captcha.value}`,
	});
	return _.identity;
};

const yaCaptchaMiddleware = (captcha: YaCaptcha) => (request: FetchPlusRequest) => {
	_.extend(request.options.headers, {
		'X-Rgs-Captcha': `${captcha.token}`,
	});
	return _.identity;
};

const statisticsHeadersMiddleware = (headers: StatisticsHeadersInterface) => request => {
	_.extend(request.options.headers, headers);
	return _.identity;
};

const statisticsQueriesMiddleware = (queries: StatisticsQueriesInterface) => request => {
	if (_.isUndefined(request.options.query)) {
		request.options.query = queries;
	} else {
		_.extend(request.options.query, queries);
	}
	return _.identity;
};


const rootLogContextMiddleware = logContextMiddleware(rootLogContext);

const setHostMiddleware = (host?: QPromise<string>) => (request: FetchPlusRequest) => {
	host = (host && forceUnwrap(host)) || location.host;

	const parsedUrl = url.parse(request.url);
	parsedUrl.protocol = parsedUrl.protocol || 'https';
	parsedUrl.host = parsedUrl.host || host;
	request.url = url.format(parsedUrl);

	return (_.identity: ServerResponse => ServerResponse);
};

const includeCredentialsMiddleware = () => (request: FetchPlusRequest) => {
	request.options.credentials = 'include';

	return (_.identity: ServerResponse => ServerResponse);
};

type SimpleMockHandler
	= Object
	| (ServerResponse, FetchPlusRequest) => ServerResponse
	;

function simpleMockApply (
	handler: ?SimpleMockHandler,
	request: FetchPlusRequest,
	response: ServerResponse,
	callback: (SimpleMockHandler, FetchPlusRequest, ServerResponse) => void
) {
	let applied = true;

	if (typeof handler === 'object') {
		response.json().catch(() => { /* ignored */ });
		response.responseJSON = handler;
	} else if (typeof handler === 'function') {
		response = handler(response, request);
	} else {
		applied = false;
	}

	if (applied) {
		invariant(handler, 'falsy handlers must not be applied');
		callback(handler, request, response);
	}
}

function simpleMockChooseHandler (handlers: *, request: *): ? SimpleMockHandler {
	return _.find(handlers, (v, k: string) => request.path.includes(k));
}

function simpleMockLog (...args) {
	if (typeof window === 'undefined') {
		// node
		return;
	}
	console.info(...args);
}

export const simpleMockMiddleware
	= (handlers: { [string]: SimpleMockHandler }): Middleware => request => ({
		after (response) {
			const handler = simpleMockChooseHandler(handlers, request);

			simpleMockApply(handler, request, response, (handler, request, response) => {
				simpleMockLog('Request mocked:', request.path, {
					request,
					response,
					handler,
				});
			});

			return response;
		},

		error (error) {
			const handler = simpleMockChooseHandler(handlers, request);

			const response: ServerResponse = (new Response("simpleMockResponse", {
				status: 200,
			}): any);

			simpleMockApply(handler, request, response, (handler, request, response) => {
				simpleMockLog('Errored request mocked:', request.path, {
					error,
					request,
					response,
					handler,
				});
			});

			return response;
		},
	});


const baseClient: ClientInterface = (new Client(): any) // WORKAROUND: flow: Recursion limit exceeded
	.withAddedMiddleware(addTrailingSlashMiddleware)
	.withAddedMiddleware(bodyTypeJSONMiddleware)
	.withAddedMiddleware(apiTuningMiddleware)
	.withAddedMiddleware(markOfrFrameModeMiddleware)
	.withAddedMiddleware(alwaysParseJSONMiddleware)
	.withAddedMiddleware(enrichNativeErrorMiddleware)
	.withAddedMiddleware(jsonEncodedErrorMiddleware)
	.withAddedMiddleware(httpErrorMiddleware)
	.withAddedMiddleware(serverApiVersioningMiddleware)
	.withAddedMiddleware(clientSourceMiddleware)
	.withAddedMiddleware(rootLogContextMiddleware)
;

const myHost: QPromise<string> = Q.all([
	config.cms.domains.get(),
])
	.spread((domains) => domains['corp.rgs.ru'])
	.catch(e => {

		if (typeof window !== 'undefined') {
			throw e;
		}

		// Only in node.js
		console.warn('Stubbing `server2` request base host');
		return 'stub.stub';
	});

const ready: Promise<void> = myHost.then(_.noop);

function forceUnwrap<T> (p: T | QPromise<T>): T {
	if (!Q.isPromise(p)) {
		return p;
	}

	p = ((p: any): QPromise<T>);

	const { state, value, error } = p.inspect();

	if (state === 'rejected') {
		throw error;
	}

	invariant(state === 'fulfilled', 'Await `server2.ready()` before using server2');

	return value;
}

const anyClient = baseClient
	.withAddedMiddleware(setHostMiddleware())
;

const myClient = baseClient
	.withAddedMiddleware(includeCredentialsMiddleware())
	.withAddedMiddleware(setHostMiddleware(myHost))
;

interface ApiCmsApi {
	api: ClientInterface;
	rest: ClientInterface;
	cmsapi: ClientInterface;
}

export interface ServerInterface {
	any: ApiCmsApi;
	my: ApiCmsApi;

	util: typeof util;

	withAuthContext: AuthContextInterface => ServerInterface;
	withLogContext: LogContextInterface => ServerInterface;
	withCaptcha: Captcha => ServerInterface;

	ready: () => Promise<void>;
}

const api: ServerInterface = {
	any: {
		api: anyClient.withAddedPath(['api']),
		rest: anyClient.withAddedPath([
			'api',
			restRoute.prefix,
			restRoute.version,
		]),
		cmsapi: anyClient.withAddedPath(['cmsapi']),
	},

	my: {
		api: myClient.withAddedPath(['api']),
		rest: myClient.withAddedPath([
			'api',
			restRoute.prefix,
			restRoute.version,
		]),
		cmsapi: myClient.withAddedPath(['cmsapi']),
	},

	util,

	withAuthContext (authContext: AuthContextInterface) {
		const clone: ServerInterface = _.extend({}, this);

		clone.my.api = clone.my.api.withAuthContext(authContext);
		clone.my.rest = clone.my.rest.withAuthContext(authContext);

		return clone;
	},

	withLogContext (logContext: LogContextInterface) {
		const clone: ServerInterface = _.extend({}, this);

		clone.my.api = clone.my.api.withLogContext(logContext);
		clone.any.api = clone.any.api.withLogContext(logContext);
		clone.my.rest = clone.my.rest.withLogContext(logContext);
		clone.any.rest = clone.any.rest.withLogContext(logContext);

		return clone;
	},

	withCaptcha (captcha: Captcha) {
		const clone: ServerInterface = _.extend({}, this);

		clone.my.api = clone.my.api.withCaptcha(captcha);
		clone.any.api = clone.any.api.withCaptcha(captcha);
		clone.my.rest = clone.my.rest.withCaptcha(captcha);
		clone.any.rest = clone.any.rest.withCaptcha(captcha);

		return clone;
	},

	withYaCaptcha (captcha: YaCaptcha) {
		const clone: ServerInterface = _.extend({}, this);

		clone.my.api = clone.my.api.withYaCaptcha(captcha);
		clone.any.api = clone.any.api.withYaCaptcha(captcha);
		clone.my.rest = clone.my.rest.withYaCaptcha(captcha);
		clone.any.rest = clone.any.rest.withYaCaptcha(captcha);

		return clone;
	},

	ready () {
		return ready;
	},
};

export default api;
